import { motion } from 'framer-motion'
import {
    Users, Calendar, ClipboardList, FileText, BarChart3,
    MessageSquare, Bell, User, Home, LogOut, BookOpen, Brain
} from 'lucide-react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'

interface MentorSidebarProps {
    activeTab: string
    setActiveTab: (tab: string) => void
}

const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'students', label: 'Assigned Students', icon: Users },
    { id: 'plan-builder', label: 'Daily Plan Builder', icon: Calendar },
    { id: 'attendance', label: 'Attendance & Discipline', icon: ClipboardList },
    { id: 'materials', label: 'Study Materials', icon: BookOpen },
    { id: 'tests', label: 'Test Management', icon: FileText },
    { id: 'question-bank', label: 'Question Bank', icon: Brain },
    { id: 'analytics', label: 'Student Analytics', icon: BarChart3 },
    { id: 'chat', label: 'Chat', icon: MessageSquare, badge: 3 },
    { id: 'announcements', label: 'Announcements', icon: Bell },
    { id: 'profile', label: 'Profile', icon: User },
]

export function MentorSidebar({ activeTab, setActiveTab }: MentorSidebarProps) {
    return (
        <GlassmorphicCard className="w-64 h-full p-4 flex flex-col">
            {/* Logo & Title */}
            <div className="mb-6">
                <div className="flex items-center gap-3 mb-1">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-blue to-blue-600 flex items-center justify-center">
                        <span className="text-white font-bold text-lg">FF</span>
                    </div>
                    <div>
                        <h2 className="font-bold text-theme-primary text-sm">Mentor Portal</h2>
                        <p className="text-xs text-theme-secondary">Dr. Sarah</p>
                    </div>
                </div>
            </div>

            {/* Navigation Menu */}
            <nav className="flex-1 space-y-1 overflow-y-auto custom-scrollbar">
                {menuItems.map((item) => {
                    const Icon = item.icon
                    const isActive = activeTab === item.id

                    return (
                        <motion.button
                            key={item.id}
                            onClick={() => setActiveTab(item.id)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all relative ${isActive
                                ? 'bg-gradient-to-r from-primary-blue to-blue-600 text-white shadow-lg shadow-blue-500/25'
                                : 'text-theme-secondary hover:bg-slate-100 dark:hover:bg-slate-800'
                                }`}
                        >
                            <Icon className="h-5 w-5 shrink-0" />
                            <span className="text-sm font-medium truncate">{item.label}</span>
                            {item.badge && (
                                <span className={`ml-auto px-1.5 py-0.5 rounded-full text-xs font-bold ${isActive
                                    ? 'bg-white/20 text-white'
                                    : 'bg-red-500 text-white'
                                    }`}>
                                    {item.badge}
                                </span>
                            )}
                        </motion.button>
                    )
                })}
            </nav>

            {/* Bottom Actions */}
            <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                <button className="w-full flex items-center gap-3 px-3 py-2.5 text-theme-secondary hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all">
                    <LogOut className="h-5 w-5" />
                    <span className="text-sm font-medium">Logout</span>
                </button>
            </div>
        </GlassmorphicCard>
    )
}
